/**
 * API Client para o modulo de Campanhas de Email
 *
 * Todas as chamadas usam o token JWT do Supabase para autenticacao.
 */

import { SUPABASE_URL } from "@/integrations/supabase/client";
import { apiFetch } from "@/lib/api-fetch";

const CAMPAIGNS_BASE_URL = `${SUPABASE_URL}c`;

function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  return apiFetch<T>(`${CAMPAIGNS_BASE_URL}${endpoint}`, options);
}

// ============================================
// Tipos
// ============================================

export interface CampaignSummary {
  id: string;
  name: string;
  campaign_type: string;
  created_at: string | null;
  total_records: number;
  pending: number;
  sent: number;
  clicked: number;
  done: number;
}

export interface CampaignSendJobResponse {
  campaign_id: string;
  status: string;
  total: number;
  sent: number;
  failed: number;
  started_at: string;
}

export interface CampaignSendStatus {
  campaign_id: string;
  db_counts: {
    total: number;
    pending: number;
    sent: number;
    clicked: number;
    done: number;
  };
  job: {
    status: string;
    total: number;
    sent: number;
    failed: number;
    started_at: string;
    finished_at: string | null;
    error: string | null;
  } | null;
}

export interface FreelancerUpdatePreview {
  eligible: number;
  total: number;
}

export interface CreateCampaignRequest {
  name: string;
  campaign_type: string;
  email_content: string;
  emails?: string[];
}

export interface FreelancerSearchResult {
  email: string;
  nome: string;
}
export interface CreateCampaignResponse {
  id: string;
  name: string;
  campaign_type: string;
  records_added: number;
}

// ============================================
// API Calls
// ============================================

export async function listCampaigns(): Promise<CampaignSummary[]> {
  return fetchWithAuth<CampaignSummary[]>("/campaigns");
}

export async function previewFreelancerUpdate(): Promise<FreelancerUpdatePreview> {
  return fetchWithAuth<FreelancerUpdatePreview>("/campaigns/preview-freelancer-update");
}

export async function createCampaign(
  request: CreateCampaignRequest
): Promise<CreateCampaignResponse> {
  return fetchWithAuth<CreateCampaignResponse>("/campaigns", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export async function getDefaultTemplate(
  campaignType: string
): Promise<{ email_content: string }> {
  return fetchWithAuth<{ email_content: string }>(
    `/campaigns/default-template/${campaignType}`
  );
}

export async function startCampaignSend(
  campaignId: string,
  limit?: number
): Promise<CampaignSendJobResponse> {
  const params = limit ? `?limit=${limit}` : "";
  return fetchWithAuth<CampaignSendJobResponse>(`/send/${campaignId}${params}`, {
    method: "POST",
  });
}

export async function getCampaignSendStatus(
  campaignId: string
): Promise<CampaignSendStatus> {
  return fetchWithAuth<CampaignSendStatus>(`/send/${campaignId}/status`);
}

export async function pauseCampaignSend(
  campaignId: string
): Promise<{ campaign_id: string; message: string }> {
  return fetchWithAuth<{ campaign_id: string; message: string }>(
    `/send/${campaignId}/pause`,
    { method: "POST" }
  );
}

